import React, { useEffect, useRef } from 'react';
import { copilotConfig } from '@components/Copilot/constants';
import DefaultPrompts from '@components/Copilot/DefaultPrompts';
import ShimmerText from '@components/Copilot/ShimmerText';
import { AgentId, ChatbotErrorType, ChatMessage, ChatRunInfo, CopilotChatMessageType, TCopilotActions } from '@components/Copilot/types';
import { formatRelativeTime, getAiMagicIcon } from '@components/Copilot/utils';
import { useAvContext } from '@context/AvContextProvider';
import { useAvCopilotContext } from '@context/AvCopilotProvider';
import { AvButton, AvMarkdown, CopyToClipboard, CopyType, Initials } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { arrayEquals, QueryKey } from '@fabric-ui/utils';
import { Box, Divider } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

interface ICopilotChatMessages {
  messages: ChatMessage[];
  isLoading?: boolean;
  onUserPromptHandler: (prompt: string) => void;
  defaultPrompts: string[];
}

const errorButtonTextMap = {
  [ChatbotErrorType.NOT_FOUND]: 'Start New Session',
};

const CopilotChatMessages: React.FC<ICopilotChatMessages> = ({ messages, isLoading = false, onUserPromptHandler, defaultPrompts }) => {
  const { user } = useAvContext();
  const queryClient = useQueryClient();
  const {
    restartCopilotSession,
    threadId,
    sessionId,
    conversationErrorMessage,
    postMessage,
    publishPrompt,
    isSummarizing,
    lastSessionProps,
    isDefaultPromptsOpen,
    setIsDefaultPromptsOpen,
    showActionButtons,
    thinkingText,
  } = useAvCopilotContext();

  const lastElement = useRef<HTMLDivElement | null>(null);

  const onErrorHandler = () => {
    if (conversationErrorMessage!.type === ChatbotErrorType.NOT_FOUND) {
      restartCopilotSession();
    } else {
      queryClient.removeQueries({ queryKey: [QueryKey.Copilot, QueryKey.PublishChatbotMessage] });
      queryClient.removeQueries({ queryKey: [QueryKey.Copilot, QueryKey.GetChatbotMessage] });
      publishPrompt({ chatRunInfo: { threadId }, userPrompt: messages.at(-1)!.content });
      queryClient.removeQueries({ queryKey: [QueryKey.Copilot, 'getAgentStatus', sessionId] });
      postMessage({ sessionId, message: messages.at(-1)!.content });
    }
  };

  useEffect(() => {
    if (lastElement?.current) {
      lastElement.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, isLoading, isDefaultPromptsOpen]);

  return (
    <Box
      sx={{
        position: 'relative',
        ...flex.col,
        gap: 4,
        pt: 3,
        width: '100%',
      }}>
      {messages.map((message, index) => {
        const showActions =
          !isLoading &&
          index === messages.length - 1 &&
          messages.length >= 2 &&
          !!lastSessionProps?.actions.length &&
          !conversationErrorMessage &&
          showActionButtons;
        return (
          <CopilotChatMessage
            key={message.timestamp}
            message={message.content}
            userName={user?.userName}
            timestamp={message.timestamp}
            type={message.type}
            state={message.state}
            lastMessageActions={showActions ? lastSessionProps.actions : []}
            chatRunInfo={{ threadId }}
            sessionId={sessionId}
          />
        );
      })}
      {isDefaultPromptsOpen && !isLoading && (
        <DefaultPrompts
          prompts={copilotConfig[lastSessionProps.agentId]?.defaultPrompts || defaultPrompts || []}
          onClick={v => {
            setIsDefaultPromptsOpen(false);
            onUserPromptHandler(v);
          }}
          agentId={lastSessionProps.agentId}
        />
      )}
      {isLoading && (
        <Box sx={{ ...flex.col, gap: 1 }}>
          <Box sx={{ ...flex.row, gap: 1 }}>
            {getAiMagicIcon(30)}
            <ShimmerText
              text={
                isSummarizing
                  ? 'Summarizing Chat Insights...'
                  : lastSessionProps.agentId === AgentId.TICKET_AGENT
                    ? 'Zscaler AI takes time to process, but it’s worth it!'
                    : thinkingText
              }
            />
          </Box>
        </Box>
      )}
      {!isLoading && conversationErrorMessage && (
        <Box sx={{ ...flex.col, gap: 1 }}>
          {getAiMagicIcon(30)}
          <AvMarkdown content={conversationErrorMessage.message} sx={{ color: theme => theme.palette.colors.neutrals[800] }} />
          <AvButton
            size="xSmall"
            variant="outlined"
            aria-label={errorButtonTextMap[conversationErrorMessage.type] || 'Retry'}
            onClick={onErrorHandler}
            sx={{ width: 'fit-content', color: theme => theme.palette.colors.neutrals[700] }}>
            {errorButtonTextMap[conversationErrorMessage.type] || 'Retry'}
          </AvButton>
        </Box>
      )}

      <Box ref={lastElement} sx={{ position: 'absolute', bottom: 0 }} />
    </Box>
  );
};

interface ICopilotChatMessage {
  message: string;
  userName: string;
  timestamp: string;
  type: CopilotChatMessageType;
  state?: any;
  lastMessageActions: TCopilotActions;
  chatRunInfo: ChatRunInfo;
  sessionId: string;
}

const CopilotChatMessage: React.FC<ICopilotChatMessage> = ({
  message,
  userName,
  timestamp,
  type,
  state,
  lastMessageActions = [],
  sessionId,
  chatRunInfo,
}) => {
  const isAiRole = [CopilotChatMessageType.CONVERSATION_MESSAGE_TYPE_AI, CopilotChatMessageType.CONVERSATION_MESSAGE_TYPE_SUMMARY].includes(
    type
  );

  return (
    <Box sx={{ ...flex.col, gap: 1 }}>
      {isAiRole ? getAiMagicIcon(30) : <Initials name={userName || ''} size="small" />}
      <AvMarkdown content={message} sx={{ color: theme => theme.palette.colors.neutrals[800] }} />
      <Box sx={{ ...flex.itemsCenter, gap: 1 }}>
        {isAiRole && (
          <>
            <CopyToClipboard copyData={message} copyType={CopyType.DATA} />
            <Divider flexItem orientation="vertical" sx={{ height: 14, alignSelf: 'center' }} />
          </>
        )}
        <Box
          sx={{
            fontSize: 12,
            color: theme => theme.palette.colors.neutrals[550],
          }}>
          {formatRelativeTime(timestamp)}
        </Box>
      </Box>
      {!!lastMessageActions.length && isAiRole && <Divider flexItem sx={{ width: '100%' }} />}
      {lastMessageActions.map(action => (
        <React.Fragment key={action.toString()}>
          {action({ content: message, type, timestamp, sessionId, chatRunInfo, state })}
        </React.Fragment>
      ))}
    </Box>
  );
};

const MemoizedCopilotChatMessages = React.memo(CopilotChatMessages, (oldProps, newProps) => {
  if (oldProps.messages.length !== newProps.messages.length) {
    return false;
  }
  if (oldProps.isLoading !== newProps.isLoading) {
    return false;
  }
  return arrayEquals(oldProps.messages, newProps.messages);
});

export default MemoizedCopilotChatMessages;
